/**
 * Slowniki i formatowanie dla kosztow (external_costs) - Etap 2 "Koszty z KSeF".
 * Kontrakt = backend/api/external_costs.py (sekcja ETAP 2) + services/koszty_service.py (zrodlo prawdy).
 * Kwoty/daty 1:1 z kontroli faktur (reuzycie). Tylko tokeny kolorow var(--...) z v3sys-theme.css.
 */
import { formatujDate, dzisISO } from '../kontrola-faktur/format';

export { formatujKwote, formatujDate, formatujMiesiac, dataISO, dzisISO } from '../kontrola-faktur/format';

/** external_costs.zrodlo */
export const ZRODLA = {
  reczny: { etykieta: 'ręczny', kolor: 'var(--text-secondary)', tlo: 'var(--bg)', opis: 'Wpisany ręcznie' },
  ksef: { etykieta: 'KSeF', kolor: 'var(--purple)', tlo: 'var(--purple-light)', opis: 'Zaksięgowany z faktury pobranej z KSeF' },
  faktura_zakupowa: { etykieta: 'faktura zakupu', kolor: 'var(--info)', tlo: 'var(--info-light)', opis: 'Z faktury zakupu towaru' },
};

/** external_costs.status */
export const STATUSY_KOSZTU = {
  planowany: { etykieta: 'Planowany', kolor: 'var(--text)', tlo: 'var(--border)' },
  niezaplacony: { etykieta: 'Do zapłaty', kolor: 'var(--warning)', tlo: 'var(--warning-light)' },
  zaplacony: { etykieta: 'Zapłacony', kolor: 'var(--success)', tlo: 'var(--success-light)' },
};

/** GET /z-ksef: wiersz.sugestia */
export const SUGESTIE_KSEF = {
  koszt: { etykieta: 'Faktura', kolor: 'var(--text)', tlo: 'var(--bg)', opis: 'Faktura kosztowa (dostawca spoza magazynu)' },
  korekta: { etykieta: 'Korekta', kolor: 'var(--orange)', tlo: 'var(--orange-light)', opis: 'Korekta kosztowa — zapis ujemny powiązany z kosztem faktury korygowanej' },
};

/** sugestia_kategorii.zrodlo -> opis dla tooltipu */
export const ZRODLA_SUGESTII = {
  regula_sklep: 'reguła dla tego sklepu',
  regula_ogolna: 'reguła ogólna (uczona z poprzednich księgowań)',
  regula_inny_sklep: 'reguła z innego sklepu',
  ostatni_koszt: 'ostatni koszt tego dostawcy',
};

export const SPOSOBY_ZAPLATY = {
  kw: { etykieta: 'KW z kasy sklepu', opis: 'Tworzy dokument KW z numerem z bramy numeracji (kasa sklepu, jak dotąd)' },
  konto: { etykieta: 'Z konta firmowego (bez KW)', opis: 'Zapłacone przelewem z konta firmy — poza kasą sklepu, bez dokumentu KW' },
};

export const MIESIACE_KROTKIE = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

/** '2026-09' -> 'wrz' */
export const etykietaMiesiaca = (yyyyMm) => {
  const m = String(yyyyMm || '').match(/^\d{4}-(\d{2})$/);
  return m ? (MIESIACE_KROTKIE[Number(m[1]) - 1] || m[1]) : String(yyyyMm || '—');
};

/** Nazwa sklepu po id; null/0 = koszt ogolny. `sklepy` = [{id, nazwa}]. */
export const nazwaSklepu = (id, sklepy = []) => {
  if (!id) return 'Ogólne (centrala)';
  const s = (sklepy || []).find(x => Number(x.id) === Number(id));
  return s ? s.nazwa : `Sklep #${id}`;
};

/** Numer KSeF skrocony do dwoch ostatnich czlonow (pelny w tooltipie). */
export const skrocNumerKsef = (numer) => {
  const t = String(numer || '').trim();
  if (!t) return '';
  const czesci = t.split('-');
  if (czesci.length < 3) return t.length <= 18 ? t : `…${t.slice(-16)}`;
  return `…${czesci.slice(-2).join('-')}`;
};

/** Komunikat bledu z interceptora api.js (err.data = cale cialo odpowiedzi backendu). */
export const komunikatBledu = (err, domyslny = 'Nie udało się wykonać operacji.') => {
  const zSerwera = err?.data?.komunikat || err?.data?.error || err?.data?.message;
  if (err?.errorCode === 'KOSZT_ISTNIEJE') return zSerwera || 'Ta faktura jest już zaksięgowana jako koszt.';
  if (err?.errorCode === 'ZLY_STATUS') return zSerwera || 'Operacja niedozwolona w tym stanie.';
  if (err?.status === 403 || err?.isPermissionError) return 'Brak uprawnienia ksiegowosc.rozliczenia.';
  return zSerwera || err?.message || domyslny;
};

/** Liczba dni po terminie (ujemna = jeszcze przed terminem); null bez terminu. */
export const dniPoTerminie = (terminISO, dzisISOStr) => {
  if (!terminISO) return null;
  const t = new Date(`${String(terminISO).slice(0, 10)}T00:00:00`);
  const d = new Date(`${String(dzisISOStr).slice(0, 10)}T00:00:00`);
  return Math.round((d - t) / 86400000);
};

// ---------------------------------------------------------------------------
// Platnosc z XML FA(3) (migracja 057; kontrakt: backend/utils/ksef_fa_parser.py + koszty_service.do_zaksiegowania)
// ---------------------------------------------------------------------------

/** FormaPlatnosci FA(3): kod -> etykieta (jak backend FORMY_PLATNOSCI). */
export const FORMY_PLATNOSCI = { 1: 'gotówka', 2: 'karta', 3: 'bon', 4: 'czek', 5: 'kredyt', 6: 'przelew', 7: 'płatność mobilna' };

/** Filtr ?forma= (kod 1-7 | 'inna' | 'brak'); klucz licznika z backendu (liczniki.formy) = etykieta | 'inna' | 'brak'. */
export const OPCJE_FORMY = [
  { id: '', etykieta: 'Forma: wszystkie', klucz: null },
  { id: '6', etykieta: 'przelew', klucz: 'przelew' },
  { id: '1', etykieta: 'gotówka', klucz: 'gotówka' },
  { id: '2', etykieta: 'karta', klucz: 'karta' },
  { id: '7', etykieta: 'płatność mobilna', klucz: 'płatność mobilna' },
  { id: '3', etykieta: 'bon', klucz: 'bon' },
  { id: '4', etykieta: 'czek', klucz: 'czek' },
  { id: '5', etykieta: 'kredyt', klucz: 'kredyt' },
  { id: 'inna', etykieta: 'inna (opis na fakturze)', klucz: 'inna' },
  { id: 'brak', etykieta: 'brak danych (bez XML)', klucz: 'brak' },
];

/** Filtr ?status= listy z KSeF (koszty_service.STANY_LISTY). */
export const STANY_LISTY_KSEF = [
  { id: 'do_zaksiegowania', etykieta: 'Do zaksięgowania' },
  { id: 'zaksiegowane', etykieta: 'Zaksięgowane' },
  { id: 'pominiete', etykieta: 'Pominięte' },
  { id: 'wszystkie', etykieta: 'Wszystkie' },
];

/** Filtr ?zaplata= (koszty_service.ZAPLATA_FILTR). */
export const OPCJE_ZAPLATY = [
  { id: '', etykieta: 'Zapłata: wszystkie' },
  { id: 'zaplacone', etykieta: 'zapłacone (wg faktury)' },
  { id: 'niezaplacone', etykieta: 'niezapłacone' },
  { id: 'po_terminie', etykieta: 'po terminie' },
  { id: 'do_zaplaty', etykieta: 'do zapłaty (w terminie)' },
  { id: 'bez_terminu', etykieta: 'bez terminu' },
];

/** '61124069608960102394954343' -> '61 1240 6960 8960 1023 9495 4343' (jak backend formatuj_rachunek). */
export const formatujRachunek = (nr) => {
  const t = String(nr || '').replace(/[\s-]/g, '').toUpperCase();
  if (!t) return '';
  let prefiks = '';
  let cyfry = t;
  if (!/^\d{2}/.test(t)) { prefiks = t.slice(0, 2); cyfry = t.slice(2); }
  if (cyfry.length < 8) return prefiks + cyfry;
  const grupy = [cyfry.slice(0, 2)];
  for (let i = 2; i < cyfry.length; i += 4) grupy.push(cyfry.slice(i, i + 4));
  return (prefiks ? `${prefiks} ` : '') + grupy.join(' ');
};

/** Skrot do tabeli: '…9495 4343' (pelny numer w title). */
export const skrocRachunek = (nr) => {
  const t = String(nr || '').replace(/[\s-]/g, '');
  if (!t) return '';
  if (t.length <= 8) return t;
  const koncowka = t.slice(-8);
  return `…${koncowka.slice(0, 4)} ${koncowka.slice(4)}`;
};

/** Stan platnosci jak backend stan_platnosci: 'zaplacona'|'po_terminie'|'do_zaplaty'|'nieznany' (zapasowo, gdy brak z API). */
export const stanPlatnosci = (w, dzis = dzisISO()) => {
  if (w?.stan_platnosci) return w.stan_platnosci;
  if (w?.zaplacono) return 'zaplacona';
  if (!w?.termin_platnosci) return 'nieznany';
  return dniPoTerminie(w.termin_platnosci, dzis) > 0 ? 'po_terminie' : 'do_zaplaty';
};

/**
 * Opis platnosci do kolumny "Płatność": {forma, stan, etykieta, kolor, tlo, title}.
 * etykieta: 'zapłacono 03.09.2026' | 'po terminie (18.09.2026, 5 dni)' | 'do zapłaty do 02.10.2026' | 'bez terminu'.
 */
export const opisPlatnosci = (w, dzis = dzisISO()) => {
  const forma = w?.forma_platnosci || null;
  const stan = stanPlatnosci(w, dzis);
  const termin = w?.termin_platnosci ? formatujDate(w.termin_platnosci) : null;
  if (stan === 'zaplacona') {
    return { forma, stan, etykieta: `zapłacono${w?.data_zaplaty ? ` ${formatujDate(w.data_zaplaty)}` : ''}`,
      kolor: 'var(--success)', tlo: 'var(--success-light)', title: 'Faktura oznaczona w KSeF jako zapłacona (Zaplacono=1)' };
  }
  if (stan === 'po_terminie') {
    const dni = dniPoTerminie(w.termin_platnosci, dzis);
    return { forma, stan, etykieta: `po terminie (${termin}, ${dni} ${dni === 1 ? 'dzień' : 'dni'})`,
      kolor: 'var(--danger)', tlo: 'var(--danger-light)', title: `Termin płatności ${termin} minął` };
  }
  if (stan === 'do_zaplaty') {
    return { forma, stan, etykieta: `do zapłaty do ${termin}`, kolor: 'var(--warning)', tlo: 'var(--warning-light)',
      title: `Termin płatności z faktury: ${termin}` };
  }
  return { forma, stan, etykieta: w?.ma_xml === false ? 'brak XML' : 'bez terminu', kolor: 'var(--text-muted)', tlo: 'var(--bg)',
    title: w?.ma_xml === false ? 'XML jeszcze nie pobrany z KSeF' : 'Faktura nie ma terminu ani znacznika zapłaty w XML' };
};
